/**
 * @module engine/shapes/registry
 * @summary Unified lookup of shape definitions + draw functions.
 * @description
 *   Merges the built-in DEFS/D with user-created custom symbols. Symbols are
 *   persisted to localStorage (cache) AND to the Cloudflare Worker API (source
 *   of truth when deployed). `loadCustom()` hydrates from localStorage
 *   immediately, then kicks off an async API sync in the background.
 */
import type { ShapeDef, DrawFn, CustomSymbolRecord } from '../../types';
import { DEFS } from './defs';
import { D } from './drawFns';
import { compileSymbolCode } from '../../lib/sanitizeSymbolCode';
import { KEYS, lsGet, lsSet } from '../../lib/storage';
import { apiGetSymbols, apiSaveSymbols, getApiCreds } from '../../lib/api';

class ShapeRegistry {
  private custom: Record<string, CustomSymbolRecord> = {};
  /** Called after a background API sync completes with new symbols. */
  private onSync: (() => void) | null = null;

  /** Set by CanvasEngine so the registry can trigger a library refresh. */
  setOnSync(cb: () => void): void {
    this.onSync = cb;
  }

  getDef(type: string): ShapeDef | undefined {
    return DEFS[type];
  }

  getDraw(type: string): DrawFn | undefined {
    return D[type];
  }

  allDefs(): Record<string, ShapeDef> {
    return DEFS;
  }

  isCustom(type: string): boolean {
    return type in this.custom;
  }

  customRecords(): Record<string, CustomSymbolRecord> {
    return this.custom;
  }

  register(type: string, def: ShapeDef, draw: DrawFn): void {
    DEFS[type] = def;
    D[type] = draw;
  }

  unregister(type: string): void {
    delete DEFS[type];
    delete D[type];
  }

  /** Add (or overwrite) a custom symbol, then reconcile with the shared store. */
  addCustom(type: string, rec: CustomSymbolRecord): void {
    this.custom[type] = rec;
    this.register(type, { label: rec.label, w: rec.w, h: rec.h, g: rec.g }, compileSymbolCode(rec.code));
    lsSet(KEYS.customSymbols, this.custom);
    this.reconcile(undefined, true).catch(() => {});
  }

  /** Delete a custom symbol locally and on the shared store. */
  removeCustom(type: string): void {
    delete this.custom[type];
    this.unregister(type);
    lsSet(KEYS.customSymbols, this.custom);
    this.reconcile(type, true).catch(() => {});
  }

  /**
   * Load custom symbols from localStorage (sync / immediate), then reconcile
   * with the Worker in the background.
   */
  loadCustom(): void {
    this.custom = lsGet<Record<string, CustomSymbolRecord>>(KEYS.customSymbols, {});
    for (const [type, rec] of Object.entries(this.custom)) {
      this.safeRegister(type, rec);
    }
    // Background reconcile — fire and forget.
    this.reconcile().catch(() => {});
  }

  /** Engine calls this on mount; kept as a thin alias over reconcile(). */
  async syncFromApi(): Promise<void> {
    return this.reconcile();
  }

  /**
   * Merge the local and remote symbol sets so NO browser's symbols are lost,
   * then push the union back so the shared store accumulates everything.
   *
   * - Union of remote + local (local wins for symbols it has, covering edits).
   * - `removed`, if given, is dropped from the union and from the server.
   * - Pushes when forced (an add/delete) OR when the server is missing a symbol
   *   this browser holds locally — which self-heals symbols that never synced.
   */
  private async reconcile(removed?: string, force = false): Promise<void> {
    let remote: Record<string, CustomSymbolRecord>;
    try {
      remote = await apiGetSymbols();
    } catch {
      return; // offline / unreachable — keep local as-is
    }

    const merged: Record<string, CustomSymbolRecord> = { ...remote, ...this.custom };
    if (removed) delete merged[removed];

    let changed = false;
    for (const [type, rec] of Object.entries(merged)) {
      const ex = this.custom[type];
      if (!ex || ex.code !== rec.code || ex.label !== rec.label || ex.g !== rec.g || ex.w !== rec.w || ex.h !== rec.h) {
        this.safeRegister(type, rec);
        changed = true;
      }
    }
    if (removed) this.unregister(removed);

    this.custom = merged;
    lsSet(KEYS.customSymbols, merged);

    // Push when forced, or when the server lacks something we hold locally.
    const serverMissing =
      Object.keys(merged).some((k) => !(k in remote)) || (!!removed && removed in remote);
    const creds = getApiCreds();
    if (creds && (force || serverMissing)) {
      apiSaveSymbols(merged, creds).catch(() => {});
    }
    if (changed || removed) this.onSync?.(); // refresh library panel
  }

  /** Register a symbol, skipping a single malformed one rather than throwing. */
  private safeRegister(type: string, rec: CustomSymbolRecord): void {
    try {
      this.register(type, { label: rec.label, w: rec.w, h: rec.h, g: rec.g }, compileSymbolCode(rec.code));
    } catch {
      /* ignore a broken custom symbol */
    }
  }
}

export const registry = new ShapeRegistry();
